import { getIndicatorByDate } from "$/api/market-monitoring/market-monitoring.api";
import { useCustomDashboard } from "$/lib/context/CustomDashboard.context";
import ArrowIcon from "$/lib/icons/ArrowIcon";
import type {
  IndicatorType,
  WidgetKey,
} from "$/lib/types/api/market-monitoring/market-monitoring.types";
import type { ThemeChoices } from "$/lib/types/General.types";
import { cn } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import Flexbox from "../../common/Flexbox";
import AreaGraph from "../../graphs/AreaGraph";
import AreaGraphWidgetTooltip from "../AreaGraphWidget/AreaGraphWidgetTooltip";
import WidgetButtons from "../WidgetButtons";
import { getDate } from "$/lib/utils/functions/date.functions";

interface Props {
  theme?: ThemeChoices;
  date: Date;
  hasChart: boolean;
  asMain: boolean;
  isDraggable?: boolean;
  id: WidgetKey;
}

const CustomAreaGraphWidget = ({
  theme = "blue",
  date,
  hasChart,
  asMain,
  isDraggable = false,
  id,
}: Props) => {
  const [selectedDate, setSelectedDate] = useState(date);

  const { data: indicatorData } = useQuery({
    queryKey: [`get-indicator-data-${id}`, id, selectedDate],
    queryFn: () =>
      getIndicatorByDate({ date: getDate(selectedDate), type: id }),
    gcTime: 0,
  });

  const {
    value,
    label,
    differenceWithSecondValue,
    differencePercentage,
    data,
  } = (indicatorData as IndicatorType) ?? {};

  const { handleDelete } = useCustomDashboard();

  const [expanded, setExpanded] = useState(true);

  const toggleExpanded = () => setExpanded((prev) => !prev);

  return (
    <Flexbox
      row
      fullHeight
      className="w-full grow gap-1 rounded-3xl bg-white p-4 shadow-sm drop-shadow-md"
    >
      <Flexbox
        fullHeight
        className={cn("w-full grow gap-2", { "gap-0": !asMain })}
      >
        <Flexbox row fullWidth justify="between" align="center">
          <h4
            className={cn("text-base font-bold", {
              "text-secondary": theme === "blue",
              "text-forest": theme === "green",
              "text-warning": theme === "orange",
              "h-[4ch] text-xl": !asMain,
            })}
          >
            {label}
          </h4>

          {isDraggable && id && (
            <WidgetButtons
              id={id}
              theme={theme}
              expanded={expanded}
              handleToggle={toggleExpanded}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              handleDelete={() => handleDelete(id)}
              isLargeItem={false}
            />
          )}
        </Flexbox>

        {expanded && (
          <AreaGraphWidgetTooltip
            className={cn("h-full w-full", {
              "h-0": !expanded,
            })}
            text="Par rapport à J-1"
          >
            <Flexbox fullWidth row className="gap-1">
              <Flexbox fullWidth align="start" className="mt-6 gap-2">
                <p
                  className={cn("text-base font-bold text-black", {
                    "text-xl": !asMain,
                  })}
                >
                  {value ? `${value} €/MWh` : "-"}
                </p>

                <Flexbox row align="center" className="gap-1">
                  <div
                    className={cn("rounded-full p-2", {
                      "bg-forest-100":
                        differencePercentage && differencePercentage >= 0,
                      "bg-error-100":
                        differencePercentage && differencePercentage < 0,
                      "bg-[#BCBCBC1A]": !differencePercentage,
                    })}
                  >
                    <ArrowIcon
                      className={cn({
                        "-rotate-90 [&_path]:!stroke-success-800":
                          differencePercentage && differencePercentage >= 0,
                        "rotate-90 [&_path]:!stroke-error-800":
                          differencePercentage && differencePercentage < 0,
                        "-rotate-90 [&_path]:!stroke-[#BCBCBC]":
                          !differencePercentage,
                      })}
                    />
                  </div>
                  <p
                    className={cn("text-sm font-semibold", {
                      "text-forest-800":
                        differencePercentage && differencePercentage >= 0,
                      "text-error-800":
                        differencePercentage && differencePercentage < 0,
                      "text-[#BCBCBC]": !differencePercentage,
                      "text-lg": !asMain,
                    })}
                  >
                    <span className="whitespace-nowrap">
                      {differenceWithSecondValue
                        ? `${differenceWithSecondValue} €/MWh`
                        : "0.00 €/MWh"}
                    </span>{" "}
                    <span className="whitespace-nowrap">
                      {differencePercentage
                        ? `(${
                            differencePercentage <= 0 ? "+" : "-"
                          } ${Math.abs(differencePercentage)}%)`
                        : ""}
                    </span>
                  </p>
                </Flexbox>
              </Flexbox>
              {hasChart && <AreaGraph theme={theme} data={data ?? []} />}
            </Flexbox>
          </AreaGraphWidgetTooltip>
        )}
      </Flexbox>
    </Flexbox>
  );
};

export default CustomAreaGraphWidget;
